import React from 'react';
import { FiShare2 } from 'react-icons/fi';
import DriveItemThumbnail from './DriveItemThumbnail';
import StorageBoxGridDndCard from './StorageBoxGridDndCard';
import StorageBoxItemActionsMenu from './StorageBoxItemActionsMenu';
import StorageBoxItemMetaBadge from './StorageBoxItemMetaBadge';
import { formatBytes } from '../api/storageBoxApi';
import { isStorageBoxBulkSelectable } from '../utils/storageBoxBulkSelect';
import { useStorageBox } from '../StorageBoxContext';

export default function StorageBoxItemGrid({
  items,
  inFavoritesView = false,
  onSelectItem,
  onOpenItem,
  onShare,
  onDelete,
  onRename,
  onQueueAudioPlayNext,
  readOnly,
  selectedId,
  publicToken = null,
  lockSessionToken = null,
  folderFileDropId = null,
  onFolderFileDragOver,
  onFolderFileDragLeave,
  onFolderFileDrop,
  dndDisabled = false,
  bulkSelectMode = false,
  bulkCheckedIds,
  onToggleBulkCheck,
  bulkSelectOptions,
}) {
  const { inLockVault, writable, browseMode } = useStorageBox();
  const inBin = browseMode === 'bin';
  const showItemActions = (item) =>
    !readOnly && (inBin ? writable : item.canManage || (inLockVault && writable));

  return (
    <div className="sb-drive-grid" role="list">
      {items.map((item) => {
        const isSelected = selectedId === item.itemId;
        const isFolder = item.type === 'folder';
        const isBulkChecked = bulkCheckedIds?.has(item.itemId);
        const canBulkSelect = isStorageBoxBulkSelectable(item, bulkSelectOptions || {});
        const cardClass = [
          'sb-drive-card',
          isSelected ? 'sb-drive-card--selected' : '',
          isBulkChecked ? 'sb-drive-card--bulk-checked' : '',
          isFolder ? 'sb-drive-card--folder' : '',
        ]
          .filter(Boolean)
          .join(' ');
        return (
          <StorageBoxGridDndCard
            key={item.itemId}
            item={item}
            disabled={dndDisabled}
            folderFileDropId={folderFileDropId}
            onFolderFileDragOver={onFolderFileDragOver}
            onFolderFileDragLeave={onFolderFileDragLeave}
            onFolderFileDrop={onFolderFileDrop}
          >
          <div role="listitem" className={cardClass}>
            {bulkSelectMode && canBulkSelect ? (
              <div className="sb-drive-card__bulk-select" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  className="sb-bulk-check"
                  checked={Boolean(isBulkChecked)}
                  onChange={() => onToggleBulkCheck?.(item.itemId)}
                  aria-label={`Select ${item.name}`}
                />
              </div>
            ) : null}
            {showItemActions(item) && !bulkSelectMode ? (
              <div className="sb-drive-card__corner">
                {item.isShared ? <FiShare2 className="sb-shared-icon sb-drive-card__shared" title="Shared" /> : null}
                <StorageBoxItemActionsMenu
                  item={item}
                  onShare={onShare}
                  onRename={onRename}
                  onDelete={onDelete}
                  onQueueAudioPlayNext={onQueueAudioPlayNext}
                  className="sb-drive-card__menu"
                />
              </div>
            ) : item.isShared ? (
              <FiShare2 className="sb-shared-icon sb-drive-card__shared sb-drive-card__shared--solo" title="Shared" />
            ) : null}
            <button
              type="button"
              className="sb-drive-card__main"
              onClick={() => {
                if (bulkSelectMode && canBulkSelect) {
                  onToggleBulkCheck?.(item.itemId);
                  return;
                }
                if (item.type === 'folder') onOpenItem?.(item);
                else onSelectItem?.(item);
              }}
              onDoubleClick={() => {
                if (bulkSelectMode) return;
                if (item.type === 'file') onOpenItem?.(item);
              }}
            >
              <span className="sb-drive-card__thumb-wrap">
                <DriveItemThumbnail
                  item={item}
                  publicToken={publicToken}
                  lockSessionToken={lockSessionToken}
                  variant="grid"
                />
                <StorageBoxItemMetaBadge item={item} inFavoritesView={inFavoritesView} variant="grid" />
              </span>
              <div className="sb-drive-card__caption">
                <span className="sb-drive-card__name" title={item.name}>{item.name}</span>
                {item.type === 'file' ? (
                  <span className="sb-drive-card__meta">{formatBytes(item.sizeBytes)}</span>
                ) : null}
              </div>
            </button>
          </div>
          </StorageBoxGridDndCard>
        );
      })}
    </div>
  );
}
